/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Segment } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { changeTool } from "../slices/footer/footerSlice";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { selectSearchSettings } from "../slices/searchSettings/searchSettingsSlice";
import { changeFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import { getSearchResultsWithIncludedResourceTypes } from "../functions/getSearchResultsAPI";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import { allFevirResourceTypes } from "../dictionaries/CodeSystemLookup";
import CodeArrayEntry from "../datatypeComponents/CodeArrayEntry";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import AlertModal from "./AlertModal";
import SaveSearchModal from "./SaveSearchModal";

// eslint-disable-next-line no-empty-pattern
const SearchQueryPage = ({ }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);
    const searchSettings = useSelector(selectSearchSettings);

    const [searchQueryState, setSearchQueryState] = useState({ "simpleString": "", "runSavedSearch": false });
    const [currentSearchResultsState, setCurrentSearchResultsState] = useState(false);
    const [fevirSearchResultsState, setFevirSearchResultsState] = useState([]);
    const [searchResultsLoadingState, setSearchResultsLoadingState] = useState(false);
    const [profileValuesState, setProfileValuesState] = useState([]);
    const [includedResourceTypeValuesState, setIncludedResourceTypeValuesState] = useState([]);
    const [selectedResourceTypeValuesState, setSelectedResourceTypeValuesState] = useState({ resourceTypeLimiter: [] });
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });
    const [saveSearchResultsClickedState, setSaveSearchResultsClickedState] = useState(false);
    const [openSaveSearchModalState, setOpenSaveSearchModalState] = useState(false);

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR Search"));
        dispatch(changeTool("FEvIR® Search"));
    }, []);

    useEffect(() => {
        if (searchSettings.source === "fevir") {
            let searchQuery = {
                "runSavedSearch": true,
                "simpleString": searchSettings.searchstring,
                "resourceTypeLimiter": searchSettings.searchparameters?.resourceTypeLimiter,
                "selectedProfiles": searchSettings.searchparameters?.selectedProfiles
            };
            if (searchSettings.searchparameters?.updatedSearchStartDate) {
                let startdate = searchSettings.searchparameters.updatedSearchStartDate;
                searchQuery.earliestDate = startdate;
            }
            setSearchQueryState(searchQuery);
        }
    }, [searchSettings]);

    useEffect(() => {
        if (searchQueryState.runSavedSearch) {
            searchClick();
        }
    }, [searchQueryState.runSavedSearch]);

    useEffect(() => {
        if (searchQueryState.resourceTypeLimiter?.length) {
            let availableProfiles = [];
            for (const resourceType of searchQueryState.resourceTypeLimiter) {
                if (ProfilesByResourceType[resourceType]) {
                    availableProfiles = availableProfiles.concat(Object.keys(ProfilesByResourceType[resourceType]));
                }
            }
            if (availableProfiles.length) {
                setProfileValuesState(availableProfiles);
            }
        }
    }, [searchQueryState.resourceTypeLimiter]);

    const processSearchSelections = async () => {
        if (searchResultsLoadingState) {
            setCurrentSearchResultsState(false);
            let selectedResourceType;
            if (searchQueryState.resourceTypeLimiter?.length === 1) {
                selectedResourceType = searchQueryState.resourceTypeLimiter[0];
            }
            let selectedProfile;
            if (searchQueryState.selectedProfiles?.length === 1) {
                selectedProfile = searchQueryState.selectedProfiles[0];
            }
            let resourceTypeLimiter;
            if (selectedResourceTypeValuesState.resourceTypeLimiter.length > 0) {
                resourceTypeLimiter = selectedResourceTypeValuesState.resourceTypeLimiter;
            } else {
                resourceTypeLimiter = searchQueryState.resourceTypeLimiter;
            }
            let earliestDate;
            if (searchQueryState.earliestDate) {
                earliestDate = searchQueryState.earliestDate;
            }

            let [searchResults, includedResourceTypeValues] = await getSearchResultsWithIncludedResourceTypes(searchQueryState.simpleString,
                selectedResourceType,
                resourceTypeLimiter,
                selectedProfile,
                searchQueryState.selectedProfiles,
                apiToken, earliestDate);

            setFevirSearchResultsState(searchResults);
            setIncludedResourceTypeValuesState(includedResourceTypeValues);
            setCurrentSearchResultsState(true);
            setSearchResultsLoadingState(false);
        }
    }

    useEffect(() => {
        processSearchSelections();
    }, [searchResultsLoadingState]);

    const searchClick = () => {
        setFevirSearchResultsState([]);
        setSearchResultsLoadingState(true);
    }

    const handleKeyUp = (event) => {
        if (event.key === 'Enter') {
            searchClick();
        }
    }

    const submitLibraryResource = async (fhirJson, title) => {
        let response = await submitFhirJson(fhirJson, title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": "Problem creating Library of resulting Resources."
                };
            });
            return null;
        }
        dispatch(changeFoiFocus(resourceFOI));
        navigate(`/resources/Library/${resourceFOI}`);
    }

    useEffect(() => {
        if (saveSearchResultsClickedState === true) {
            let searchString = searchQueryState.simpleString ||
                searchQueryState.selectedProfiles?.join(",") ||
                searchQueryState.resourceTypeLimiter?.join(",");
            let title = "FEvIR Search Results for " + searchString.substring(0, 40) + " for " + userName;
            let fhirJson = {
                "resourceType": "Library", "title": title, "status": "active",
                "type": {
                    "coding": [{
                        "system": "http://terminology.hl7.org/CodeSystem/library-type",
                        "code": "asset-collection", "display": "Asset Collection"
                    }]
                },
                "date": new Date().toISOString(),
                "description": "",
                "relatedArtifact": []
            };

            if (fevirSearchResultsState?.length > 0) {
                fhirJson.description = "Your search includes " + fevirSearchResultsState.length + " Resources.";
                for (const result of fevirSearchResultsState) {
                    fhirJson.relatedArtifact.push({
                        "type": "contains",
                        "resourceReference": result.data
                    });
                }
            } else {
                fhirJson.description = "No Resources were found on your search.";
            }
            fhirJson.description += " Your search string was: " + searchQueryState.simpleString + ". ";
            if (searchQueryState.selectedProfiles?.length > 0) {
                fhirJson.description += "Your search results were limited to Profiles of: " + searchQueryState.selectedProfiles.join(", ") + ".";
            }
            if (searchQueryState.resourceTypeLimiter?.length > 0) {
                fhirJson.description += "Your search results were limited to Resources of type: " + searchQueryState.resourceTypeLimiter.join(", ") + ".";
            }
            submitLibraryResource(fhirJson, title);
        }
    }, [saveSearchResultsClickedState]);

    return <div>
        <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
        <SaveSearchModal openSaveSearchModalState={openSaveSearchModalState}
                setOpenSaveSearchModalState={setOpenSaveSearchModalState}
                searchParameters={{ resourceTypeLimiter: searchQueryState.resourceTypeLimiter, selectedProfiles: searchQueryState.selectedProfiles }}
                source={"fevir"} searchString={searchQueryState.simpleString}
                header={"Label your FEvIR search"}
                searched={currentSearchResultsState} />
        <Segment className={`containerSegment maxRemainderOfPageSegment`} raised textAlign="left">
            <TextField className="inputField" type="text" size="small"
                style={{ width: "75%" }}
                label="Simple Search String" variant="outlined"
                value={searchQueryState.simpleString || ""} onKeyUp={handleKeyUp}
                onChange={(e) => { setSearchQueryState(prevState => { return { ...prevState, "simpleString": e.target.value } }); }}
            />
            <br /><br />
            <CodeArrayEntry elementName='resourceTypeLimiter' fieldLabel='Limit by Resource Type'
                allowedValues={allFevirResourceTypes} dataEntryStyle='dropdownsearch'
                startingValue={searchQueryState.resourceTypeLimiter || []}
                setResourceState={setSearchQueryState} />
            <p style={{ paddingTop: "12px" }}>Limit to resource types of: {(searchQueryState.resourceTypeLimiter &&
                searchQueryState.resourceTypeLimiter.length > 0) ?
                searchQueryState.resourceTypeLimiter.join(', ') : "[No limits]"}</p>
            <br />
            {profileValuesState.length ?
                <>
                    <CodeArrayEntry elementName='selectedProfiles' fieldLabel='Limit by Profile'
                        allowedValues={profileValuesState.sort()}
                        dataEntryStyle='dropdownsearch'
                        startingValue={searchQueryState.selectedProfiles || []}
                        setResourceState={setSearchQueryState} />
                    <p style={{ paddingTop: "12px" }}>
                        Limit to Profiles of: {(searchQueryState.selectedProfiles &&
                            searchQueryState.selectedProfiles.length > 0) ?
                            searchQueryState.selectedProfiles.join(', ') : "[No limits]"}</p>
                    <br />
                </>
                :
                <></>
            }
            <span>
                <Button style={{ color: "#000000", width: "360px", float: "left" }}
                    className="formButton"
                    content="Search"
                    onClick={searchClick} />
                &nbsp;&nbsp;
                <span style={{display: 'inline-block', marginTop: "2px"}}>
                    <Button className="formButton positive" content={"Save Search"} compact
                        onClick={async () => { setOpenSaveSearchModalState(true); }}
                    />
                </span>
            </span>
            <br />   <br />
            {searchResultsLoadingState ?
                <><img style={{ height: "22px" }} src="/spinner.gif" alt="Loading" /> Loading...</>
                :
                <div style={{ marginTop: "18px" }}>
                    <hr />
                    {(fevirSearchResultsState.length > 0 && currentSearchResultsState) && <>
                        <p>
                            {fevirSearchResultsState.length} FEvIR search results.
                        </p>
                        <Button className="formButton positive"
                            content={"Save Search Results"} compact
                            onClick={() => { setSaveSearchResultsClickedState(true); }}
                        />
                        <br /><br />
                        <CodeArrayEntry elementName='resourceTypeLimiter'
                            fieldLabel='Limit Results by Resource Type'
                            allowedValues={includedResourceTypeValuesState} dataEntryStyle='dropdownsearch'
                            startingValue={selectedResourceTypeValuesState.resourceTypeLimiter || []}
                            setResourceState={setSelectedResourceTypeValuesState} />
                        <br />
                        {fevirSearchResultsState.map((result, resultIndex) => {
                            return <p key={resultIndex}>
                                <a href={result.onClick} >{result.display}</a>
                            </p>
                        })}
                    </>}
                    {(!(fevirSearchResultsState.length > 0) && currentSearchResultsState) &&
                        "No matching entries on FEvIR Platform."}
                </div>
            }
        </Segment>
    </div>
}

export default SearchQueryPage;